import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Table,
} from "reactstrap";
import {
    FaCalculator,
    FaCoins,
    FaHandHoldingUsd,
    FaInfoCircle,
    FaSyncAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";
import zakatService from "../../services/zakatService";

const today = () => new Date().toISOString().slice(0, 10);

const oneYearAgo = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setDate(date.getDate() + 1);
    return date.toISOString().slice(0, 10);
};

const formatMoney = (value) =>
    new Intl.NumberFormat("fr-MA", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
    }).format(Number(value || 0));

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const componentTypeBadge = (type) => {
    if (type === "ASSET") {
        return { color: "success", label: "Actif" };
    }
    if (type === "LIABILITY") {
        return { color: "warning", label: "Dette" };
    }
    return { color: "info", label: "Info" };
};

const ZakatCalculatorPage = () => {
    const [filters, setFilters] = useState({
        dateFrom: oneYearAgo(),
        asOfDate: today(),
        nisab: 0,
    });
    const [summary, setSummary] = useState(null);
    const [components, setComponents] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadSummary = async () => {
        setLoading(true);
        try {
            const data = await zakatService.summary(filters);
            setSummary(data);
            setComponents(
                (data.components || []).map((component) => ({
                    ...component,
                    adjustedAmount: component.amount,
                    included: component.included !== false,
                }))
            );
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Calcul impossible",
                text: error?.response?.data?.message || error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculated = useMemo(() => {
        const assets = components
            .filter((component) => component.type === "ASSET" && component.included)
            .reduce((sum, component) => sum + toNumber(component.adjustedAmount), 0);
        const liabilities = components
            .filter((component) => component.type === "LIABILITY" && component.included)
            .reduce((sum, component) => sum + toNumber(component.adjustedAmount), 0);
        const base = Math.max(0, assets - liabilities);
        const nisab = toNumber(filters.nisab);
        const aboveNisab = base >= nisab;
        const zakatDue = aboveNisab ? base * 0.025 : 0;

        return {
            assets,
            liabilities,
            base,
            aboveNisab,
            zakatDue,
        };
    }, [components, filters.nisab]);

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleComponentChange = (key, patch) => {
        setComponents((prev) =>
            prev.map((component) =>
                component.key === key ? { ...component, ...patch } : component
            )
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        loadSummary();
    };

    const resetAdjustments = () => {
        setComponents((prev) =>
            prev.map((component) => ({
                ...component,
                adjustedAmount: component.amount,
                included: component.included !== false,
            }))
        );
    };

    return (
        <div>
            <Row className="g-3 mb-3">
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Actifs zakatables</div>
                            <h4 className="mb-0">{formatMoney(calculated.assets)}</h4>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Dettes deductibles</div>
                            <h4 className="mb-0">{formatMoney(calculated.liabilities)}</h4>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Base zakat</div>
                            <h4 className="mb-0">{formatMoney(calculated.base)}</h4>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Zakat estimee 2.5%</div>
                            <h4 className="mb-0 text-success">{formatMoney(calculated.zakatDue)}</h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm mb-3">
                <CardBody>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <h4 className="mb-0 d-flex align-items-center">
                            <FaHandHoldingUsd className="text-success me-2" />
                            Calculateur Zakat
                        </h4>
                        <Badge color={calculated.aboveNisab ? "success" : "secondary"}>
                            {calculated.aboveNisab ? "Au-dessus du nisab" : "Sous le nisab"}
                        </Badge>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row className="g-2 align-items-end">
                            <Col md={3}>
                                <FormGroup className="mb-0">
                                    <Label>Debut periode</Label>
                                    <Input
                                        type="date"
                                        name="dateFrom"
                                        value={filters.dateFrom}
                                        onChange={handleFilterChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className="mb-0">
                                    <Label>Date de calcul</Label>
                                    <Input
                                        type="date"
                                        name="asOfDate"
                                        value={filters.asOfDate}
                                        onChange={handleFilterChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className="mb-0">
                                    <Label>Nisab</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        name="nisab"
                                        value={filters.nisab}
                                        onChange={handleFilterChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <div className="d-flex gap-2">
                                    <Button color="primary" className="flex-fill" disabled={loading}>
                                        <FaSyncAlt className="me-1" />
                                        Actualiser
                                    </Button>
                                    <Button type="button" color="light" onClick={resetAdjustments}>
                                        Reset
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
            </Card>

            <Alert color="info" className="d-flex align-items-start gap-2">
                <FaInfoCircle className="mt-1" />
                <div>
                    Les montants sont pre-calcules depuis les enregistrements de l'application.
                    Ajustez les lignes si certains montants doivent etre exclus ou corriges avant validation comptable.
                </div>
            </Alert>

            <Card className="shadow-sm">
                <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0 d-flex align-items-center">
                            <FaCalculator className="text-primary me-2" />
                            Composants du calcul
                        </h5>
                        <div className="text-muted small">
                            Source: {summary?.dateFrom || filters.dateFrom} au {summary?.asOfDate || filters.asOfDate}
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table hover className="align-middle">
                            <thead>
                                <tr>
                                    <th>Inclure</th>
                                    <th>Element</th>
                                    <th>Type</th>
                                    <th>Montant app</th>
                                    <th>Montant retenu</th>
                                    <th>Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : components.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            Aucun composant disponible.
                                        </td>
                                    </tr>
                                ) : (
                                    components.map((component) => (
                                        <tr key={component.key}>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={component.included}
                                                    disabled={component.type === "INFO"}
                                                    onChange={(event) =>
                                                        handleComponentChange(component.key, {
                                                            included: event.target.checked,
                                                        })
                                                    }
                                                />
                                            </td>
                                            <td className="fw-semibold">{component.label}</td>
                                            <td>
                                                {(() => {
                                                    const badge = componentTypeBadge(component.type);
                                                    return <Badge color={badge.color}>{badge.label}</Badge>;
                                                })()}
                                            </td>
                                            <td>{formatMoney(component.amount)}</td>
                                            <td style={{ width: 180 }}>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={component.adjustedAmount}
                                                    onChange={(event) =>
                                                        handleComponentChange(component.key, {
                                                            adjustedAmount: event.target.value,
                                                        })
                                                    }
                                                />
                                            </td>
                                            <td className="small text-muted">{component.source}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>

            <Card className="shadow-sm mt-3">
                <CardBody>
                    <Row className="g-3 align-items-center">
                        <Col md={8}>
                            <div className="d-flex align-items-center">
                                <FaCoins className="text-success me-2" size={24} />
                                <div>
                                    <div className="text-muted small">Montant recommande</div>
                                    <h3 className="mb-0">{formatMoney(calculated.zakatDue)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col md={4} className="text-md-end">
                            <div className="text-muted small">Formule</div>
                            <strong>(Actifs - Dettes) x 2.5%</strong>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </div>
    );
};

export default ZakatCalculatorPage;
